<template>
  <!-- eslint-disable max-len -->
  <div id="app">
    <div class="effectivedate">
      <p>Effective February 1, 2021</p>
    </div>
    <h1>YOUR PRIVACY MATTERS</h1>
    <p>
      Thank you for visiting Synapse Group, Inc., doing business as 4YourHealth
      (the “Company,” “we,” or “us”), a division of
      <a
        href="https://www.meredith.com/"
        target="_blank"
        rel="noopener noreferrer"
        >Meredith</a
      >
      Corporation. This Privacy Statement describes our practices in connection
      with Personal Information that we collect through:
    </p>
    <ul>
      <li>
        Websites operated by us from which you are accessing this Privacy Statement
        (the “Sites”) including
        <a
          href="http://www.4yourhealth.com/"
          target="_blank"
          rel="noopener noreferrer"
          >4YourHealth</a
        >
      </li>
      <li>
        Software applications made available by us for use on or through
        computers and mobile devices (the “Apps”);
      </li>
      <li>
        HTML-formatted e-mail messages that we send to you that link to this
        Privacy Statement or other communications with you; and
      </li>
      <li>Offline business interactions you may have with us.</li>
    </ul>
    <p>
      Collectively, we refer to the Websites, Apps, e-mails, and offline business
      interactions as the “Services.”
    </p>
    <p>
      You can read this Privacy Statement in full <a href="#personal">here</a>. Here are
      the highlights:
    </p>

    <Table :id="id" />

    <Section name="personal">
      <p>
        <span class="bold">"Personal Information"</span> is information that
        identifies you as an individual or relates to an identifiable
        individual. The Services collect Personal Information, including:
      </p>
      <ul>
        <li>Name</li>
        <li>Age or date of birth</li>
        <li>Postal address (including billing and shipping addresses)</li>
        <li>Telephone number</li>
        <li>E-mail address</li>
        <li>
          IP address (we may also derive your approximate location from your IP
          address)
        </li>
        <li>Profile picture</li>
        <li>Social media account ID</li>
        <li>Relationship status</li>
        <li>Payment card data</li>
       <li>Voice and audio information (call records for product/service assessment and improvement purposes).</li>
       <li>Other Personal Information that Consumers Voluntarily Choose to Provide: Any content you submit or make available when you use the interactive features on our Services (like photos and comments), as well as the Personal Information about others that you may share in order to send others (friends, relatives, colleagues, etc.) information that may interest them.</li>
     </ul>
        <p>
        <span class="bold">"Other Information"</span> is any information that
        does not reveal your specific identity or does not directly relate to an
        identifiable individual. The Services collect Other Information such as:
      </p>
      <ul>
        <li>Browser and device information</li>
        <li>App usage data</li>
        <li>
          Information collected through cookies, pixel tags and other
          technologies
        </li>
        <li>
          Demographic information and Other Information provided by you that
          does not reveal your specific identity
        </li>
        <li>
          Information that has been aggregated in a manner such that it no
          longer reveals your specific identity
        </li>
      </ul>
    </Section>

    <Section name="collect">
      <p>
        We and our service providers may collect Other Information in a variety
        of ways, including:
      </p>
      <ul>
        <li class="bold">From You.</li>
        <ul>
          <li>
            We collect Personal Information through the Services, for example,
            when you sign up for a newsletter, register an account to access the
            Services, or make a purchase.
          </li>
          <li>
            We also collect Personal Information from you offline, e.g., when
            you place an order over the phone, or contact customer service.
          </li>
        </ul>
        <li class="bold">From Other Sources.</li>
        <ul>
          <li>
            We receive your Personal Information from other sources, for
            example:
          </li>
          <ul>
            <li>publicly available databases;</li>
            <li>
              joint marketing partners, when they share the Personal Information with
              us.
            </li>
          </ul>
          <li>
            If you connect your social media account to your Services account,
            you will share certain Personal Information from your social media
            account with us, for example, your name.
          </li>
        </ul>
      </ul>
      <p>
        We need to collect Personal Information in order to provide the
        requested Services to you. If you do not provide the Personal Information
        requested, we may not be able to provide the Services. If you disclose
        any Personal Information relating to other people to us or to our
        service providers in connection with the Services, you represent that
        you have the authority to do so and to permit us to use the information
        in accordance with this Privacy Statement.   .
      </p>
      <h4>Collection of Other Information</h4>
      <p>
        We and our service providers may collect Other Information in a variety
        of ways, including:
      </p>
      <ul>
        <li class="bold">Your browser or device.</li>
        <ul>
          <li>
            Certain information is collected by most browsers or automatically
            through your device, such as your Media Access Control (MAC)
            address, computer type (Windows or Mac), screen resolution,
            operating system name and version, device manufacturer and model,
            language, Internet browser type and version and the name and version
            of the Services (such as the App) you are using. We use this
            information to ensure that the Services function properly.
          </li>
        </ul>
        <li class="bold">Your use of the App.</li>
        <ul>
          <li>
            When you download and use the App, we and our service providers may
            track and collect App usage data, such as the date and time the App
            on your device accesses our servers and what information and files
            have been downloaded to the App based on your device number.
          </li>
        </ul>
        <li class="bold">Cookies.</li>
        <ul>
          <li>
            Cookies are pieces of information stored directly on the computer
            that you are using. We and our service providers use the information collected
            from cookies for security purposes, to facilitate navigation, to display
            information more effectively, and to personalize your experience. We
            also gather statistical information about use of the Services in
            order to continually improve their design and functionality,
            understand how they are used, and assist us with resolving questions
            regarding them. If you do not want information collected through the
            use of cookies, most browsers allow you to automatically decline
            cookies or be given the choice of declining or accepting a
            particular cookie (or cookies) from a particular website. You may
            also wish to refer to the Cookie Management
            <a
              href="http://www.allaboutcookies.org/manage-cookies/index.html"
              target="_blank"
              rel="noopener noreferrer"
              >webpage</a
            >. If, however, you do not accept cookies, you may experience some
            inconvenience in your use of the Services. You also may not receive
            advertising or other offers from us that are relevant to your
            interests and needs.
          </li>
        </ul>
        <li class="bold">Pixel tags.</li>
        <ul>
        - Pixel tags (also known as web
            beacons and clear GIFs) may be used to, among other things, track
            the actions of users of the Services (including e-mail recipients),
            measure the success of our marketing campaigns, and compile
            statistics about usage of the Services and response rates.
          </ul>
        <li class="bold">Adobe Flash technology</li>
        <ul>
        -  We may use Adobe Flash technology (including Flash Local Shared Objects (“Flash LSOs”)) and other technologies to, among other things, collect and store information about your use of the Services.
             </ul>
             <li>
            <span class="bold">Analytics.</span> We use Google Analytics, which uses cookies and similar technologies to collect and analyze information about use of the Services and report on activities and trends.  This service may also collect information regarding the use of other websites, apps and online resources.  You can learn about Google’s practices by visiting Google’s Privacy Policy
            <a
              href="http://www.google.com/policies/privacy/partners/"
              target="_blank"
              rel="noopener noreferrer"
              >webpage</a
            >.
            </li>
      </ul>
    </Section>

    <Section name="use">
      <p>
        We and our service providers use your Personal Information for the following
        purposes:
      </p>
      <ul  id="underscore">
        <li>
          <span class="bold"
            >Providing the functionality of the Services and fulfilling your
            requests.</span
          >
          <ul>
            <li>
              To provide the Services’ functionality to you, such as arranging
              access to your registered account, facilitating communications
              with telemedicine providers, and providing you with related
              customer service.
            </li>
            <li>
              To respond to your inquiries and fulfill your requests, when you
              contact us via one of our online contact forms or otherwise, for
              example, when you send us questions, suggestions, compliments or
              complaints, or when you request a quote or other information
              about our Services.
            </li>
            <li>
              To complete your transactions, verify Personal information, and
              provide you with related customer service.
            </li>
            <li>
              To send administrative information to you, such as changes to our
              terms, conditions, and policies.
            </li>
            <li>
              To allow you to send messages to another person through the
              Services if you choose to do so.
            </li>
          </ul>
        </li>
        <li>
          <span class="bold"
            >Providing you with our newsletter and/or other marketing materials
            and facilitating social sharing.</span
          >
          <ul>
            <li>
              To send you marketing related e-mails, with information about our
              services, new products and other news about our company.
            </li>
            <li>
              To facilitate social sharing functionality that you choose to use.
            </li>
          </ul>
        </li>
        <li>
          <span class="bold"
            >Analyzing Personal Information for business reporting and providing
            personalized services.</span
          >
          <ul>
            <li>
              To analyze or predict our users’ preferences in order to prepare
              aggregated trend reports on how our digital content is used, so we
              can improve our Services.
            </li>
            <li>
              To better understand your interests and preferences, so that we
              can personalize our interactions with you and provide you with
              information and/or offers tailored to your interests.
            </li>
            <li>
              To better understand your preferences so that we can deliver
              content via our Services that we believe will be relevant and
              interesting to you.
            </li>
          </ul>
        </li>
        <li>
          <span class="bold"
            >Allowing you to participate in sweepstakes, contests or other
            promotions.</span
          >
          <ul>
            <li>
              We may offer you the opportunity to participate in a sweepstakes,
              contest or other promotion.
            </li>
            <li>
              Some of these promotions have additional rules containing
              information about how we will use and disclose your Personal
              Information. Please read those additional rules before choosing to
              participate.
            </li>
          </ul>
        </li>
        <li>
          <span class="bold"
            >Aggregating and/or anonymizing Personal Information.</span
          >
          <ul>
            <li>
              We may aggregate and/or anonymize Personal Information so that it
              will no longer be considered Personal Information. We do so to
              generate other data for our use, which we may use and disclose for
              any purpose, as it no longer identifies you or any other
              individual.
            </li>
          </ul>
        </li>
        <li>
          <span class="bold">Accomplishing our business purposes.</span>
          <ul>
            <li>
              For data analysis, for example, to improve the efficiency of our
              Services;
            </li>
            <li>
              For audits, to verify that our internal processes function as
              intended and to address legal, regulatory, or contractual
              requirements;
            </li>
            <li>
              For fraud and security monitoring purposes, for example, to detect
              and prevent cyberattacks or attempts to commit identity theft;
            </li>
            <li>For developing new products and services;</li>
            <li>
              For enhancing, improving, repairing, maintaining, or modifying our
              current products and services, as well as undertaking quality and
              safety assurance measures;
            </li>
            <li>
              For identifying usage trends, for example, understanding which
              parts of our Services are of most interest to users;
            </li>
            <li>
              For determining the effectiveness of our promotional campaigns, so
              that we can adapt our campaigns to the needs and interests of our
              users; and
            </li>
            <li>
              For operating and expanding our business activities, for example,
              understanding which parts of our Services are of most interest to
              our users so we can focus our energies on meeting our users’
              interests.
            </li>
          </ul>
        </li>
      </ul>
      <h4>Uses and Disclosures of Other Information</h4>
      <p>
        We may use and disclose Other Information for any purpose, except where
        we are required to do otherwise under applicable law. If we are required
        to treat Other Information as Personal Information under applicable law,
        we may use and disclose it for the purposes for which we use and
        disclose Personal Information as detailed in this Privacy Statement. In some
        instances, we may combine Other Information with Personal Information.
        If we do, we will treat the combined information as Personal Information
        as long as it is combined.
      </p>
    </Section>

    <Section name="share">
      <p>We disclose Personal Information:</p>
      <ul>
        <li class="bold">
          To our parent company, Synapse Group, Inc. for the purposes
          described in this Privacy Statement.
        </li>
        <li class="bold">To other companies owned by the Meredith Corporation. We may share Personal Information with other companies owned by the Meredith Corporation, which have their own privacy statements and are not subject to this Privacy Statement.
        </li>
        <li>
          <span class="bold"
            >To our third-party service providers, to facilitate services they
            provide to us.</span
          >
          <ul>
            <li>
              These can include providers of services such as website hosting,
              data analysis, payment processing, order fulfillment, information
              technology and related infrastructure provision, customer service,
              e-mail delivery, auditing, and other services.
            </li>
          </ul>
        </li>
        <li class="bold">
          To third parties, to permit them to send you marketing communications,
          consistent with your choices.
        </li>
        <li class="bold">
          To third-party providers of healthcare-related services including
          healthcare provider video conferencing or phone hotlines, as further
          described below.
        </li>
        <li class="bold">
          To third-party sponsors of sweepstakes, contests, and similar
          promotions.
        </li>
        <li>
          <span class="bold"
            >By using the Services, you may elect to disclose Personal
            Information.</span
          >
          <ul>
            <li>
              On message boards, chat, profile pages, blogs, and other services
              to which you are able to post Personal Information and content. Please note
              that any Personal Information you post or disclose through these services
              will become public and may be available to other users and the
              general public.
            </li>
            <li>
              Through your social sharing activity. When you connect your
              Services account to your social media account, you will share
              Personal Information with your friends associated with your social media
              account, with other users, and with your social media account
              provider. By doing so, you authorize us to facilitate this sharing
              of Personal Information, and you understand that the use of shared
              Personal Information will be governed by the social media provider’s
              privacy policy.
            </li>
          </ul>
        </li>
      </ul>
      <h4>Other Uses and Disclosures</h4>
      <p>
        We also use and disclose your Personal Information as necessary or
        appropriate, in particular when we have a legal obligation or legitimate
        interest to do so:
      </p>
      <ul>
        <li>
          <span class="bold"
            >To comply with applicable law and regulations.</span
          >
          <ul>
            <li>This may include laws outside your country of residence.</li>
          </ul>
        </li>
        <li>
          <span class="bold"
            >To cooperate with public and government authorities.</span
          >
          <ul>
            <li>
              To respond to a request or to provide Personal Information we believe is
              necessary or appropriate.
            </li>
            <li>
              These can include authorities outside your country of residence.
            </li>
          </ul>
        </li>
        <li>
          <span class="bold">To cooperate with law enforcement.</span>
          <ul>
            <li>
              For example, when we respond to law enforcement requests and
              orders or provide Personal Information we believe is important.
            </li>
          </ul>
        </li>
        <li>
          <span class="bold">For other legal reasons.</span>
          <ul>
            <li>To enforce our terms and conditions; and</li>
            <li>
              To protect our rights, privacy, safety or property, and/or that of
              our affiliates, you or others.
            </li>
          </ul>
        </li>
        <li>
          <span class="bold"
            >In connection with a sale or business transaction.</span
          >
          <ul>
            <li>
              We have a legitimate interest in disclosing or transferring your
              Personal Information to a third-party in the event of any
              reorganization, merger, sale, joint venture, assignment, transfer,
              or other disposition of all or any portion of our business,
              assets, or stock (including in connection with any bankruptcy or
              similar proceedings).
            </li>
          </ul>
        </li>
      </ul>
      <h4>Personal Information Shared With Your Telehealth Care Provider</h4>
      <p>
        The Services allow you to share your Personal Information, including
        your sensitive health information, with your telehealth care provider
        such as when engaging in telehealth video conferences or communications
        with licensed physicians or nurses. Your telehealth care provider will
        receive your contact Personal Information from your 4YourHealth account.
      </p>
      <p>
        Your electronic health record and any other Personal Information you disclose on
        the telehealth care provider site or in communications with a telehealth
        care provider is collected by the telehealth care provider. 4YourHealth
        is not responsible for, and does not have any control over, the
        Personal Information that you choose to share directly with your telehealth care
        provider. If you have any questions or concerns regarding your
        electronic health record, please contact the telehealth care provider
        directly as detailed at 800-611-5601.
      </p>
      <p>
        Health care providers including myTelemedicine and <span class="double_underline">Album Health</span> that you
        may be redirected to through Services may be subject to HIPAA and are
        solely responsible for complying with privacy and data security laws
        applicable to health care providers with respect to any of your health
        information provided to myTelemedicine or eDoc America. Health care
        providers that are subject to HIPAA are required by law to provide you
        with a notice of their privacy practices and respond to your requests
        for amendment or access of your health information and an accounting of
        disclosures of your health information. You can exercise these rights
        and any other rights you may have under applicable law by contacting
        myTelemedicine directly as detailed at 800-611-5601 and eDoc America
        directly at 866 525 3362.
        </p>
        </Section>
        <Section name="control">
      <h3>What Rights Do You Have?</h3>
      <article>
        <ul>
          <li>
            <span class="bold">Right to Know:</span> You have the right to ask
            us to tell you what personal information we collect use, disclose
            and sell about you.
          </li>
          <br />
          <li>
            <span class="bold">Right to Request Deletion:</span> You have the
            right to ask us to delete the personal information we collect or
            maintain about you.
          </li>
                <br />
          <li>
            <span class="bold">Right to be Free from Discrimination:</span> You
            have the right not to be discriminated against for exercising these
            rights.
          </li>
            <br />
          <li>
            <span class="bold"
              >Right to Access or Correct Personal Information:</span
            >
            You have the right to request access to and to edit certain personal
            information that you have provided to us through our Services.
          </li>
        </ul>
      </article>
      <article>
      <p>If you are in the U.S. and wish to exercise your rights:</p>
      <ul>
      <li>
     <span class="bold"> Information and Deletion Requests. </span>To exercise your right to information on our data collection practices on our Services or to request deletion of Personal Information on our Services, please use the request form available <a
              href="https://privacyportalde-cdn.onetrust.com/dsarwebform/2159c482-749a-49db-916b-475017a9efa5/855e3b71-3dd3-4546-977c-88c7430ade09.html"
              target="_blank"
              rel="noopener noreferrer"
              >here</a
            >. We will need to verify that you are who you say you are before we delete or share Personal Information with you.  You may need to provide us with a copy of your utility bill so that we can verify your identity. After you submit your request, you will be sent a confirmation e-mail and need to click on the link provided. If you are a postal subscriber, you will need to confirm your subscription information in order to verify your request.
      </li>
      <br />
      <li>
     <span class="bold"> Authorized Agents </span>You can also designate an authorized agent to make a request on your behalf. If you use an authorized agent, please include a written permission that you have designated that agent to make the request, or proof of the agent‘s power of attorney, We may follow-up with you to verify your identity before processing your authorized agent’s request.
      </li>
      </ul>
      </article>
                    <h4>What Other Choices Do You Have?</h4>
      <p>
        We give you choices regarding our use and disclosure of your Personal
        Information for marketing purposes. You may opt out from:
      </p>
      <ol>
        <li>
          <span class="bold">Receiving marketing-related e-mails from us.</span>
          If you no longer want to receive marketing related e-mails from us on a
          going-forward basis, you may opt out
          <a
            :href="'http://privacy.synapsegroupinc.com/optout.html?id=4YH'"
            target="_blank"
            rel="noopener noreferrer"
            >here</a
          >.
        </li>
        <br />
        <li>
          <span class="bold">Receiving text/SMS/mobile messages from us.</span>
          If you no longer want to receive text/SMS/mobile messages from us on a
          going-forward basis, you may opt out by replying “STOP” to the
          message.
        </li>
      </ol>
      <p>
        We will try to comply with your request(s) as soon as reasonably
        practicable. Please note that if you opt out of receiving marketing
        related e-mails from us, we may still send you important administrative
        messages, from which you cannot opt out.
      </p>
      <h4>Cookies & Similar Technologies.</h4>
      <p>
        If you do not want information collected through the use of cookies, most browsers allow you to automatically decline cookies or be given the choice of declining or accepting a particular cookie (or cookies) from a particular website.  You may also wish to refer to the Cookie Management
        <a
              href="http://www.allaboutcookies.org/manage-cookies/index.html"
              target="_blank"
              rel="noopener noreferrer"
              >webpage</a
            >.
            . If, however, you do not accept cookies, you may experience some inconvenience in your use of the Services.  You also may not receive advertising or other offers from us that are relevant to your interests and needs.
      </p>
      <p>
        Note that you must opt-out separately from each computer or device and browser that you use to access our Sites and if you clear your cookies, you will need to repeat the opt-out process.
      </p>
      <ul id="nonebullet">
      <ul>
      <li>
         <span class="bold">Google Analytics:</span>
          You can exercise the opt-out provided by Google by downloading the Google Analytics <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              >webpage</a
            > browser add-on.
        </li>
        <br />
        <li>
         <span class="bold">Adobe Flash Technology:</span>
          If you do not want Flash LSOs stored on your computer, you can adjust the settings of your Flash player to block Flash LSO storage using the tools contained in the  <a
              href="http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager07.html"
              target="_blank"
              rel="noopener noreferrer"
              >Website Storage Settings Panel</a
            >.  You can also go to the <a
              href="http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager03.html"
              target="_blank"
              rel="noopener noreferrer"
              >Global Storage Settings Panel</a
            > and follow the instructions (which may explain, for example, how to delete existing Flash LSOs (referred to as “information”), how to prevent Flash LSOs from being placed on your computer without your being asked, and how to block Flash LSOs that are not being delivered by the operator of the page you are on at the time).  Please note that setting the Flash Player to restrict or limit acceptance of Flash LSOs may reduce or impede the functionality of some Flash applications.
          </li>
          <br /></ul> </ul>
      <h4 class="bold">Opt-Out of Interest-Based Advertising</h4>
       <p>From time to time, we work with third -party partners that engage in targeted or interest-based advertising.</p>
              <ul>
              <li>
              If you go to optout.aboutads.info or www.aboutads.info/choices, you can learn how to exercise choice regarding the collection of information about your online activities over time and across multiple third-party websites, online services, devices, and applications for interest-based advertising purposes.
              </li>
              <br />
              <li>
              Most of the third -party advertising and data companies we work with, including Google are also members of the Network Advertising Initiative ("NAI"). To learn more about the information collection practices of NAI members and your related opt-out choices, please visit the  <a
              href="http://optout.networkadvertising.org/?c=1"
              target="_blank"
              rel="noopener noreferrer"
              >NAI's website.</a
            >.
              </li>
              </ul>
               <h4 class="bold">Do Not Track</h4>
               <p>4YourHealth does not recognize the “do not track signals” that some browsers may employ.</p>
               <h4 class="bold">Third-Party Services</h4>
               <p>This Privacy Statement does not address, and we are not responsible for, the privacy, information, or other practices of any third parties, including any third party operating any website or service to which the Services link.  The inclusion of a link on the Services does not imply endorsement of the linked site or service by us or by our affiliates.</p>
               <p>In addition, we are not responsible for the information collection, use, disclosure, or security policies or practices of other organizations, such as Facebook, Apple, Google, Microsoft, RIM, or any other app developer, app provider, social media platform provider, operating system provider, wireless service provider, or device manufacturer, including with respect to any Personal Information you disclose to other organizations through or in connection with the Apps.</p>
                   </Section>
    <Section name="children">
      <p>
        The Services are not directed to individuals under the age of sixteen.
      </p>
    </Section>

    <Section title="Additional Privacy Information">
      <ul>
        <li>
          <p>
            <span class="bold">Sensitive Information.</span> Unless we request
            it, we ask that you not send us, and you not disclose, any sensitive
            Personal Information (e.g., social security numbers, information
            related to racial or ethnic origin, political opinions, religion or
            other beliefs, biometrics or genetic characteristics, criminal
            background) on or through the Services or otherwise to us.
          </p>
        </li>
        <li>
          <p>
            <span class="bold">Third-Party Payment Service.</span> We may use a
            third party payment service to process payments made through the
            Services. If you wish to make a payment through the Services, your
            Personal Information will be collected by such third party and not
            by us, and will be subject to the third party’s privacy policy,
            rather than this Privacy Statement. We have no control over, and are
            not responsible for, this third party’s collection, use, and
            disclosure of your Personal Information.
          </p>
        </li>
        <li>
          <p>
            <span class="bold"
              >Security, Retention and Storage of Information.</span
            >
            We seek to use reasonable organizational, technical and
            administrative measures to protect Personal Information within our
            organization. Unfortunately, no data transmission or storage system
            can be guaranteed to be 100% secure. If you have reason to believe
            that your interaction with us is no longer secure, please
            immediately notify us in accordance with the
            <span class="italic">"How Can You Learn More"</span> section below.
          </p>
        </li>
<li>
          <p>
            <span class="bold"
              >Location of Your Data.</span
            >
            Meredith is a U.S. company and your Personal Information will be transferred to, processed, stored and accessed by us, the Meredith Companies and Service Providers in the United States and in other jurisdictions where we or they operate, which may not provide you with the same level of privacy protection as you are entitled to in your jurisdiction. When you visit our Services, you consent to the processing of your information Personal Information in the United States and other jurisdictions as described here.
                    </p>
        </li>
     <li>
          <p>
            <span class="bold">EU-U.S. Privacy Shield.</span> Meredith adheres to the principles of the EU-U.S. Privacy Shield Framework (“Privacy Shield Principles”) as set forth by the U.S. Department of Commerce regarding the collection, use, and retention of Personal Information transferred from the European Union (“EU”) to the United States; however, we do not rely on the EU-U.S. Privacy Shield Framework when we transfer or receive Personal Information transferred from the EU. If there is any conflict between the terms in this Privacy Policy and the Privacy Shield Principles, the Privacy Shield Principles will govern. To learn more about the Privacy Shield program, please visit the Privacy Shield website.
          </p>
          <p>
            Meredith is responsible for the processing of Personal Information
            it receives under the EU-U.S. Privacy Shield Framework, or
            subsequently transfers to a third party acting as an agent on its
            behalf. Meredith complies with the Privacy Shield Principles for all
            onward transfers from the EU and EEA, including onward transfer
            liability provisions.
          </p>
          <p>
            The U.S. Federal Trade Commission has jurisdiction over Meredith’s
            compliance with its obligations under the EU-U.S. Privacy Shield. In
            certain situations, Meredith may be required to disclose Personal Information in response to a lawful request by public authorities,
            including to meet national security or law enforcement requirements.
          </p>
          <p>
            In compliance with the Privacy Shield Principles, Meredith commits
            to resolve complaints about our collection or use of your personal
            information. EU individuals with inquiries or complaints regarding
            our Privacy Shield policy should first contact us at
            <a href="mailto:privacy@synapsemail.com">privacy@synapsemail.com</a
            >.</p>
            <p>Meredith has further committed to refer unresolved Privacy Shield complaints to JAMS, an established alternative dispute resolution provider located in the United States. If you do not receive timely acknowledgment of your complaint
            from us, or if we have not resolved your complaint, Principlesplease contact or visit JAMS or call 800-352-5267 to file a complaint.  If the arbitrator finds that you cannot afford to pay JAMS's filing, administrative, hearing and/or other fees and you cannot obtain a waiver from JAMS, Meredith will pay them for you.
          </p>
        </li>
        <li>
          <p>
            <span class="bold">Changes to Our Privacy Policy.</span> Digital
            technology is rapidly evolving. If we decide to change our Privacy
            Statement in the future, we'll post the changes here and indicate at
            the top of the page the last date on which it was updated. Unless
            otherwise noted, all changes will be effective when posted.
                        </p>
        </li>
      </ul>
    </Section>
    <Section name="section4">
      <p>
        This section contains information on the personal information we collect
        about residents of California (our “California Consumers”), including
        the sources of that information, how that informed is used, who it is
        shared with, and the rights that California Consumers have under
        California law. Our collection of your information, our data sharing
        practices, and your privacy rights are discussed below.
      </p>
      <ol class="roman">
        <li>Our Collection of Your Information</li>
        <h3 class="privacy1">What Personal Information Do We Collect?</h3>
        <ul>
        <li class="disc">
       <span class="bold">Contact Information</span>(Name, e-mail, postal address, zip code, telephone number)
        </li>
        <li class="disc">
        <span class="bold">Account/Registration Information </span> (Name, username, and password)
        </li>
        <li class="disc">
        <span class="bold">Payment Information </span>(such as user name and password, profile picture, reminder questions and answers, and communications preferences)
        </li>
         <li class="disc">
        <span class="bold">Demographic Information</span> (age, race, gender)
        </li>
         <li class="disc">
        <span class="bold">Online Identifiers</span> (IP addresses, device IDs, and other internal and third-party identifiers we use to recognize you online)
        </li>
         <li class="disc">
        <span class="bold">Location Data</span> (like your address, city, and zip code, collected in limited circumstances; we may also derive your approximate location from your IP address)
        </li>
         <li class="disc">
        <span class="bold">Online Activity Information</span> (browsing history, search history, information about your interactions with our Services)
        </li>
         <li class="disc">
        <span class="bold">Interests, Preferences and other Behavioral Data </span>(including purchase behaviors)
        </li>
         <li class="disc">
        <span class="bold">Voice and Audio Information</span>
        </li>
         <li class="disc">
        <span class="bold">Other Personal Information that Consumers Voluntarily Choose to Provide</span> (photos, comments, or Personal Information about others that you post or submit on our Services)
        </li>
                  </ul>
                   <h3 class="privacy1">What are our Sources of Personal Information?</h3>
          <ul>
            <li class="disc">
       <span class="bold">You</span> (when you register on our Services, subscribe to one of our publications, or otherwise submit Personal Information to us).
        </li>
<li class="disc">
        <span class="bold">Our Advertisers who place ads on Services.</span>
        </li>
        <li class="disc">
        <span class="bold">Our Data Partners </span>(these are companies who give us Personal Information about you to help us and our Advertisers understand who you are and what you may be interested in)
        </li>
        <li class="disc">
        <span class="bold">Pixels, Cookies, and Other Tracking Technologies,</span>which collect Personal Information about you when you visit our Services online.
        </li>
                   </ul>
                     <h3 class="privacy1">How Do We Use Personal Information?</h3>
                    <p class="underline"> To Provide the Services to You: </p>
                    <ul>
                     <li class="disc">
                       <span class="bold">To contact you </span>(to deliver our newsletters, surveys, and other communications to you)
                     </li>
                     <br />
                      <li class="disc">
                       <span class="bold">To fulfill your subscriptions,</span>orders and other requests.
                     </li>
                      <br />
                      <li class="disc">
                       <span class="bold">To create and manage your account</span>when you register on our Services, and provide you with your membership benefits.
                     </li>
                      <br />
<li class="disc">
                       <span class="bold">To geofence certain portions of our website </span>(we restrict certain portions of our website to visitors from the United States and we use your location information for that purpose)
                     </li>
                      <br /> </ul>
  <p class="underline">For Advertising & Marketing: </p>
  <ul>
  <li class="disc"> <span class="bold">To market our Services to you. </span>
  </li>
  <br />
 <li class="disc"> <span class="bold">To combine with other Personal Information we receive from our Data Partners to help us learn more about you, your interests </span>and preferences so that we can customize your experience on our Services (including the content and advertisements displayed to you).
  </li>
   <br />
 <li class="disc"> <span class="bold">To create consumer segments </span>(groups of cookies and other IDs based on Personal Information we collect about, for example “Women age 35-44 who have purchased mascara”, which we share with advertisers looking to sell mascara or products we think people who like mascara may also be interested in).
  </li>
   <br />
   <li class="disc"> <span class="bold">To deliver targeted advertisements to you on social media platforms or other third-party websites. </span>
  </li>
   <br />
   <li class="disc"> <span class="bold">To serve targeted ads to you</span>(to help us, our advertisers, and our advertising networks and platforms to identity the best ads to display to you on our Services, based on the Interests, Preferences and Other Behavioral Data associated with your online identifiers).
  </li>
   <br />
   <li class="disc"> <span class="bold">To help us identify you and understand your interests </span>interests and preferences so that we can customize your experience on our Services (including the content and advertisements displayed to you).
  </li>
   <br />
   <li class="disc">To <span class="bold">help our Advertisers  </span>contact you, directly or online.
  </li>
   <br />
  </ul>
  <p class="underline">For Auditing our Interactions:</p>
  <ul>
   <li class="disc">To help  <span class="bold">us measure the effectiveness of the ads </span>on our Services, including to understand whether you click on the ads on our Services and whether you buy the products/services advertised.
  </li>
  <br />
  <li class="disc"> <span class="bold">To help us assess and improve our Sites </span>(we record telephone calls between you and Synapse Group, Inc.)
    </li>
  </ul>
  <p class="nounderline">For Our and Your Security</p>
  <ul>
   <li class="disc">To <span class="bold">prevent fraud</span>, including verifying your identity
  </li>
  <br />
   <li class="disc">To <span class="bold">detect security incidents.</span>
  </li>
  </ul>
            <li>Our Data Sharing Practices</li>
        <p>
          We share the Personal Information below with our Service Providers.
          Our Service Providers help us send our newsletters to you, fulfill
          your orders, provide advertising and marketing services, and
          analytics, and other activities that support our business Service
          Providers only use the Personal Information we share with them to
          provide these services to us.
        </p>
        <p>
          Below are the categories of Personal Information we disclose to our
          Service Providers:
        </p>
        <article>
          <ul>
            <li class="disc"><span class="bold">Contact Information</span></li>
            <br />
           <li class="disc"><span class="bold">Demographic Information</span></li><br />
            <li class="disc"><span class="bold">Online Identifiers</span></li><br />
            <li class="disc"><span class="bold">Online activity information</span></li><br />
            <li class="disc"><span class="bold">Payment Information</span></li><br />
             <li class="disc"><span class="bold">Interests, Preferences and other Behavioral Data  </span></li><br />
              <li class="disc"><span class="bold">Voice and Audio Information  </span></li><br />
               <li class="disc"><span class="bold">Other Personal Information that Consumers Voluntarily Choose to Provide  </span></li><br />
                </ul><br />
              <span class="bold">Personal Information We Sell to Third Parties</span><br /><br />
              In limited circumstances, we share your personal information Personal Information with Data Partners and Advertisers which may use that information for their own commercial purposes.
              Below are the categories of personal information Personal Information that we sell to third parties:
<ul><br />
              <li class="disc"><span class="bold">Contact</span></li><br />
               <li class="disc"><span class="bold">Information </span></li><br />
                <li class="disc"><span class="bold">Online Identifiers</span></li><br />
                 <li class="disc"><span class="bold">Online Activity Information</span></li><br />
                 <li class="disc"><span class="bold">Interests, Preferences and other Behavioral Data </span></li><br />
              </ul>
                                                                                          </article>
               <!-- eslint-disable-next-line max-len -->
              <li>
          Your Privacy Rights
          <h4>What Rights Do You Have?</h4>
          <ul class="not-bold">
            <li class="disc">
              <!-- eslint-disable-next-line max-len -->
              <p>
                <span class="bold">Shine the Light Rights </span> – You have the
                right to request and obtain from us, once a year and free of
                charge, a list of the third parties to whom we have disclosed
                certain types of Personal Information(if any) for their direct
                marketing purposes in the prior calendar year. At our option, we
                may respond to such requests by providing instructions about how
                our users can exercise their options to prevent our disclosure
                of Personal Information to third parties for their direct
                marketing purposes.
              </p>
            </li>
            <li class="disc">
              <!-- eslint-disable-next-line max-len -->
              <p>
                <span class="bold"
                  >Right to Opt-Out of a Sale of Personal Information
                </span>
                – You have the right to ask us not to sell Personal Information to
                third parties.
              </p>
            </li>
          </ul>
        </li>
        <h4>How Do You Submit These Requests to Us?</h4>
        <!-- eslint-disable-next-line max-len -->
        <p>
          In addition to the rights detailed in the
          <a href="#control"
            >"How Can You Control Our Use of Your Personal Information?"</a
          >
          section above, California residents can:
        </p>
        <ul>
          <li class="disc">
            Contact us to exercise your Shine the Light Rights at
            <a href="mailto:privacy@synapsemail.com">privacy@synapsemail.com</a
            >.
          </li>
          <li class="disc" >
            <span class="bold">Opt-Out of Sales.</span> To opt-out of the sale
            of your Personal Information on this website click
            <a
              href="https://privacyportalde-cdn.onetrust.com/dsarwebform/2159c482-749a-49db-916b-475017a9efa5/424d0d20-a84d-41ed-9a27-379d31f2551d.html"
              target="_blank"
              rel="noopener noreferrer"
              >here</a
            >.
          </li>
        </ul>
      </ol>
    </Section>

    <Section name="learn">
      <h3>How to Contact Us</h3>
      <p>
        If you have any questions or concerns about this Privacy Statement or
        its implementation please contact us via email at
        <a href="mailto:privacy@synapsemail.com">privacy@synapsemail.com</a> or
        by writing to:
      </p>
      <h3>4YourHealth</h3>
      <p>
        c/o Synapse Group, Inc. <br />
        Attention: Privacy Department <br />
        225 High Ridge Rd <br />
        Stamford, CT 06905
      </p>
      <p>
        Because email communications are not always secure, please do not
        include credit card or other sensitive information in your emails to us.
      </p>
    </Section>
  </div>
</template>

<script>
import Table from './components/Table.vue';
import Section from './components/Section.vue';

export default {
  name: 'app',
  props: {
    id: String,
  },
  components: {
    Table,
    Section,
  },
};
</script>

<style lang="scss">
#app {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  margin-top: 60px;
  max-width: 1200px;
  margin: auto;
}
a {
  color: #42b983;
}
p,
h3,
h4,
h5 {
  padding: 0 20px;
}
h1 {
  text-align: center;
}
ol.roman {
  list-style: upper-roman;
  > li {
    margin-top: 10px;
    font-size: 18px;
    font-weight: 600;
  }
}

#underscore ul {
   list-style: none; /* Remove list bullets */
  > li {
    display: list-item;
  list-style-type: "-";
  padding-inline-start: 1ch;
          }}

          #nonebullet ul {
   list-style: none; /* Remove list bullets */
   margin-left: -4rem;
     }
table {
  display: block;
  overflow-x: scroll;
  border: 1px solid black;

  tr,
  td,
  th {
    border: 1px solid black;
  }

  td {
    vertical-align: top;
    min-width: 250px;
  }
}
.bold {
  font-weight: 700;
}
.not-bold {
  font-weight: initial;
  font-size: 16px;
}
.italic {
  font-style: italic;
}
.privacy1{
 margin-left: -3rem;
 }
.disc{
  list-style-type: disc;
  margin-left: -1rem;
}
.underline{
   margin-left: -3rem;
  text-decoration: underline;
}
.double_underline{
  border-bottom: 3px double;
}
.nounderline{
    margin-left: -3rem;
 }
</style>
